import { ProductVariantModel } from '#models/productVariant';
import { UserPriceStatus, useUserStore } from '~/store/user';
import NP from 'number-precision';
import { useGlobalContentStore } from '~/store/globalContent';
import { CartApiItem } from '#constants/types/norce';
import { ParametricData } from '#constants/types/algolia';
import { roundPrice } from '~/util/priceRounding';

// removes a lot of warning when dividing with a lot of decimals
NP.enableBoundaryChecking(false);

type UnitConversionType = 'UnitConversionTypeMetric' | 'UnitConversionTypeImperial';

type UnitConversionCode = 'm' | 'm2' | 'SquareFeet' | 'RunningFeet' | 'sq. ft' | 'Inch' | 'sq.ft' | 'sq. yards' | 'sq.yards';

export type ConvertedPrice = {
  status: UserPriceStatus;
  price?: {
    price: number,
    priceBeforeDiscount: number,
    priceBeforeDiscountBeforeVat: number,
    priceBeforeVat: number,
    recommendedPrice: number,
    recommendedPriceBeforeVat: number,
  };
}

/**
 * The input has to be a reactive variant of ProductVariantModel
 * @example usage from a props:
 * const { conversionType } = usePackageAreaConverter(toRef(()=>props.activeVariant));
 * @see https://vuejs.org/api/reactivity-utilities#toref
 *
 * But if input is already reactive, skip toRef:
 * @example
 * const quickBuyActiveVariant = ref<ProductVariantModel>(productHydrated.variants[0]);
 * const { conversionType } = usePackageAreaConverter(quickBuyActiveVariant);
 *
 * @param input - reactive version of ProductVariantModel
 *
 */
export default function usePackageAreaConverter(input: Readonly<Ref<ProductVariantModel>> | Readonly<Ref<CartApiItem>>) {
  const userStore = useUserStore();

  const globalContent = useGlobalContentStore();
  const unitConversionType: UnitConversionType = globalContent.getMeasurement === 'metric' ? 'UnitConversionTypeMetric' : 'UnitConversionTypeImperial';

  const versionParametricName = unitConversionType === 'UnitConversionTypeMetric' ? 'UnitConversionMetric' : 'UnitConversionImperial';

  /*  const hasConversion = input.value.productParametrics ?
    input.value.productParametrics?.find(p => p.code === unitConversionType) :
    input.value.infoTypes?.find(p => p.name === unitConversionType + 'Code');*/

  const hasConversion = computed(()=> {
    // Product variant
    if (input.value?.productParametrics) {
      const findVariant =  input.value.productParametrics?.find(p => p.code === unitConversionType);
      if (findVariant && findVariant.listValue) {
        return {
          code: findVariant.listValue.code,
          name: findVariant.listValue.name || findVariant.listValue.code, // Fallback to code if name is null
        };
      }
      return null;
    } else if (input.value.infoTypes) {
      // Cart api
      const code = input.value.infoTypes?.find(p => p.name === unitConversionType + 'Code');
      const name = input.value.infoTypes?.find(p => p.name === unitConversionType + 'Name');

      if (code) {
        return {
          code: code.value,
          name: name?.value || code.value,
        };
      }

    }
    return null;
  });

  const unitDisplay = hasConversion.value?.name || '';

  const userPrice = computed(()=> {
    return userStore.getPrice(input.value.partNo, false);
  });

  const versionParametric = computed(()=> {
    const exists = input.value.parametrics?.find(p => p.code === versionParametricName);
    if (exists) {
      return exists.value;
    }
    const exists2 = input.value.infoTypes?.find(p => p.name === versionParametricName);
    if (exists2) {
      return exists2.value;
    }
    return null;
  });

  const conversionType = computed<UnitConversionCode | null>(() => {
    if (hasConversion.value?.code && versionParametric.value) {
      return hasConversion.value.code as UnitConversionCode;
    }

    if (globalContent.getIsB2b && hasConversion.value?.code) {
      console.error(`Product: ${input.value.partNo} is set to conversion type ${hasConversion.value?.code} but no version parametric is set`);
    }

    return null;
  });

  /**
   * Wait for price to be loaded from the price api then convert to area prices
   */
  const convertPackageToAreaprice = computed<ConvertedPrice>(()=> {
    if (userPrice.value.status !== 'ok') {
      return {
        status: userPrice.value.status,
      };
    }
    const price = parseFloat(userPrice.value.price?.price?.replaceAll(',', '') || '');
    const priceBeforeDiscount = parseFloat(userPrice.value.price?.priceBeforeDiscount?.replaceAll(',', '') || '');
    const priceBeforeDiscountBeforeVat = parseFloat(userPrice.value.price?.priceBeforeDiscountBeforeVat?.replaceAll(',', '') || '');
    const priceBeforeVat = parseFloat(userPrice.value.price?.priceBeforeVat?.replaceAll(',', '') || '');
    const recommendedPrice = parseFloat(userPrice.value.price?.priceBeforeVat?.replaceAll(',', '') || '');
    const recommendedPriceBeforeVat = parseFloat(userPrice.value.price?.priceBeforeVat?.replaceAll(',', '') || '');

    // Error check
    if (isNaN(price)) {
      return {
        status: 'error',
      };
    }

    if (hasConversion.value && versionParametric.value) {
      return {
        status: userPrice.value.status,
        price: {
          price: roundPrice(NP.divide(price, versionParametric.value)),
          priceBeforeDiscount: roundPrice(NP.divide(priceBeforeDiscount, versionParametric.value)),
          priceBeforeDiscountBeforeVat: roundPrice(NP.divide(priceBeforeDiscountBeforeVat, versionParametric.value)),
          priceBeforeVat: roundPrice(NP.divide(priceBeforeVat, versionParametric.value)),
          recommendedPrice: roundPrice(NP.divide(recommendedPrice, versionParametric.value)),
          recommendedPriceBeforeVat: roundPrice(NP.divide(recommendedPriceBeforeVat, versionParametric.value)),
        },
      };
    }

    return {
      status: 'error',
    };
  });

  const areaPerPackage = computed<number | null>(() => {
    return versionParametric.value;
  });

  /**
   * Convert a number of packs to an area
   * @param packs - number of packs
   */
  const convertPackageToArea = (packs: number) => {
    if (areaPerPackage.value) {
      return NP.times(areaPerPackage.value, packs);
    }
    return null;
  };

  /**
   * Converts a price (not loaded from api) to area price
   * @param price
   */
  const convertPackagePriceToAreaPrice = (price: number | string) => {
    if (typeof price === 'string') {
      // Remove all commas (thousands separators for SE market) and spaces, then parse
      price = parseFloat(price.replace(/,/g, '').replace(/ /g, ''));
    }
    if (areaPerPackage.value) {
      return roundPrice(NP.divide(price, areaPerPackage.value));
    }
    return null;
  };

  const shouldUseArea = computed(() => {
    if (globalContent.getIsB2b && hasConversion.value?.code && !versionParametric.value) {
      console.error(`Product: ${input.value.partNo} is set to conversion type ${hasConversion.value?.code} but no version parametric is set`);
      return false;
    }

    return conversionType.value === 'm'
      || conversionType.value === 'm2'
      || conversionType.value === 'SquareFeet'
      || conversionType.value === 'sq. ft'
      || conversionType.value === 'sq.ft'
      || conversionType.value === 'Inch'
      || conversionType.value === 'sq.yards'
      || conversionType.value === 'sq. yards'
      || conversionType.value === 'RunningFeet';
  });

  return {
    conversionType,
    userPrice,
    convertPackageToAreaprice,
    areaPerPackage,
    unitDisplay,
    shouldUseArea,
    convertPackageToArea,
    convertPackagePriceToAreaPrice,
    hasConversion,
  };
}
